import React from "react";
import { Users, Scale, ClipboardCheck, Wallet } from "lucide-react";
import { COMPANY } from "../content";
import { useLanguage } from "../i18n";
import { PageHero, PillButton, Reveal, SectionTitle } from "../components/Shared";

const ROLE_ICONS = [Users, ClipboardCheck, Scale, Wallet];

const Team = () => {
  const { t, data } = useLanguage();

  return (
    <div data-testid="team-page">
      <PageHero
        title={t.teamPage.title}
        subtitle={t.teamPage.subtitle}
        image="https://images.pexels.com/photos/1181370/pexels-photo-1181370.jpeg?w=1920&q=80"
      />

      {/* Présentation équipe */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center">
          <Reveal>
            <SectionTitle>{t.teamPage.sectionTitle}</SectionTitle>
            <p className="text-slate-600 leading-loose font-light">
              {t.teamPage.teamText1}
              <strong className="font-semibold text-[#1b2653]">{COMPANY.name}</strong>
              {t.teamPage.teamText2}
            </p>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
            {data.teamRoles.map((r, i) => {
              const Icon = ROLE_ICONS[i % ROLE_ICONS.length];
              return (
                <Reveal key={r.id} delay={i * 100}>
                  <div className="bg-[#f5f7fb] rounded-lg p-7 h-full hover:shadow-lg hover:-translate-y-1 transition-all duration-400">
                    <div className="w-12 h-12 rounded-full bg-[#1b2653] flex items-center justify-center mx-auto mb-5">
                      <Icon size={22} className="text-white" />
                    </div>
                    <h3 className="font-semibold text-[#1b2653] mb-2.5">{r.title}</h3>
                    <p className="text-slate-600 text-[13.5px] leading-relaxed font-light">{r.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Fondateur - biographie complète */}
      <section className="py-24 bg-[#f5f7fb]" data-testid="founder-bio-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-14">
            <div className="lg:col-span-2">
              <Reveal>
                <div className="lg:sticky lg:top-28">
                  <div className="relative max-w-sm mx-auto lg:mx-0">
                    <div className="absolute -top-4 -right-4 w-full h-full rounded-lg bg-[#2547a8]/10" />
                    <img
                      src={data.founder.photo}
                      alt={data.founder.name}
                      className="relative rounded-lg shadow-xl w-full object-cover"
                    />
                  </div>
                  <div className="mt-8 text-center lg:text-left">
                    <h2 className="text-2xl font-semibold text-[#1b2653]">{data.founder.name}</h2>
                    <p className="text-[#2547a8] font-medium mt-1.5 text-sm">{data.founder.role}</p>
                  </div>
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-3">
              <Reveal delay={100}>
                <span className="text-[#2f6fd8] text-xs font-semibold uppercase tracking-[0.25em]">
                  {t.teamPage.founderLabel}
                </span>
                <h2 className="text-3xl font-semibold text-[#1b2653] mt-3 mb-7 leading-snug">
                  {data.founder.heading}
                </h2>
                <p className="text-slate-700 leading-loose font-normal mb-5 text-[15px] border-l-4 border-[#2547a8] pl-5 italic">
                  {data.founder.intro}
                </p>
                {data.founder.bio.map((p, i) => (
                  <p key={i} className="text-slate-600 leading-loose font-light mb-5 text-[15px]">
                    {p}
                  </p>
                ))}
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#1b2653] py-20">
        <div className="max-w-4xl mx-auto px-5 text-center">
          <Reveal>
            <h2 className="text-2xl md:text-3xl font-semibold text-white leading-snug">{t.teamPage.ctaTitle}</h2>
            <p className="text-white/70 font-light mt-4">{t.teamPage.ctaText}</p>
            <div className="mt-9">
              <PillButton to="/rendez-vous">{t.requestRdv}</PillButton>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Team;
